import { Component } from '@theme/component';

/**
 * SD: Einfacher Slider auf Basis von nativem horizontalem Scrollen mit Scroll-Snap.
 * - Pfeile blättern um eine Kachel und werden nur gezeigt, wo es weitergeht
 * - Fortschrittslinie zeigt, wie weit gescrollt ist
 * Ob überhaupt gescrollt wird, entscheidet das CSS (z. B. nur auf dem Handy).
 *
 * - Mit Attribut disable-arrows: Pfeile am Anfang/Ende deaktivieren statt ausblenden
 * - Mit Attribut adaptive-arrows: Jeder Pfeil bekommt data-tone="dark" oder "light",
 *   je nachdem ob das Bild an seiner Position hell oder dunkel ist (für schwarze bzw. weiße Pfeile)
 *
 * Markup: <sd-slider> mit ref="track" (scrollt), ref="prev", ref="next", ref="progress".
 *
 * @typedef {{ track: HTMLElement, prev?: HTMLButtonElement, next?: HTMLButtonElement, progress?: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class SdSlider extends Component {
  requiredRefs = ['track'];

  /** @type {number | null} */
  #rafId = null;

  #resizeObserver = new ResizeObserver(() => this.#update());

  /**
   * Eigene Teile des Sliders. Nicht this.refs: Liegen Slider ineinander (Produktreihe mit Produktkarten),
   * landen beim Nachladen sonst Teile der inneren Slider in den refs des äußeren.
   * @type {Refs}
   */
  #parts = /** @type {Refs} */ ({});

  connectedCallback() {
    super.connectedCallback();
    this.#parts = this.#findParts();
    this.#parts.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver.observe(this.#parts.track);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#parts.track?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver.disconnect();
  }

  /** @returns {Refs} */
  #findParts() {
    /** @param {string} name */
    const own = (name) =>
      [...this.querySelectorAll(`[ref="${name}"]`)].find((element) => element.closest('sd-slider') === this);
    return /** @type {Refs} */ ({ track: own('track'), prev: own('prev'), next: own('next'), progress: own('progress') });
  }

  previous() {
    this.#scrollBySlide(-1);
  }

  next() {
    this.#scrollBySlide(1);
  }

  /** Pfeile schwarz auf hellem, weiß auf dunklem Bildbereich */
  #updateArrowTones() {
    const { track, prev, next } = this.#parts;
    if (!prev && !next) return;

    const index = Math.round(track.scrollLeft / (track.clientWidth || 1));
    const img = track.children[index]?.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    if (!img.complete || !img.naturalWidth) {
      img.addEventListener('load', () => this.#updateArrowTones(), { once: true });
      return;
    }

    const edges = measureEdges(img);
    if (!edges) return;
    if (prev) prev.dataset.tone = edges.left > 0.6 ? 'dark' : 'light';
    if (next) next.dataset.tone = edges.right > 0.6 ? 'dark' : 'light';
  }

  /** @param {number} direction */
  #scrollBySlide(direction) {
    const track = this.#parts.track;
    const slide = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = slide instanceof HTMLElement ? slide.offsetWidth + gap : track.clientWidth;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #onScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update() {
    const { track, prev, next, progress } = this.#parts;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollable = maxScroll > 1;

    this.toggleAttribute('scrollable', scrollable);

    // Kleine Toleranz, weil Snap-Positionen auf halbe Pixel fallen können.
    // Mit Attribut disable-arrows werden Pfeile am Ende deaktiviert statt ausgeblendet (z. B. Pfeile in einer Kopfzeile).
    const atStart = !scrollable || track.scrollLeft <= 1;
    const atEnd = !scrollable || track.scrollLeft >= maxScroll - 1;
    const state = this.hasAttribute('disable-arrows') ? 'disabled' : 'hidden';
    if (prev) prev[state] = atStart;
    if (next) next[state] = atEnd;

    if (this.hasAttribute('adaptive-arrows')) this.#updateArrowTones();

    // Fortschrittsbalken: Länge = Anteil ganz sichtbarer Kacheln (z. B. 1 von 3 = 1/3),
    // Position = wie weit gescrollt (0 bis 1). Angeschnittene Nachbarn zählen nicht mit.
    if (progress) {
      const slides = track.children.length;
      const slide = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const slideWidth = slide instanceof HTMLElement ? slide.offsetWidth + gap : track.clientWidth;
      const perView = Math.max(1, Math.floor((track.clientWidth + gap) / slideWidth));
      const size = scrollable && slides > 0 ? Math.min(1, perView / slides) : 1;
      const position = scrollable ? Math.min(1, Math.max(0, track.scrollLeft / maxScroll)) : 0;
      progress.style.setProperty('--sd-slider-size', String(size));
      progress.style.setProperty('--sd-slider-position', String(position));
    }
  }
}

/** Ergebnisse pro Bild merken, damit jedes Bild nur einmal gemessen wird */
const toneCache = new Map();

/** Kleine Leinwand zum Messen (wird wiederverwendet) */
const canvas = document.createElement('canvas');
const context = canvas.getContext('2d', { willReadFrequently: true });

/**
 * Misst die Helligkeit des Bildes an den Pfeil-Positionen (links und rechts, vertikal mittig).
 * Berücksichtigt den Zuschnitt durch object-fit: cover.
 * @param {HTMLImageElement} img
 * @returns {{ left: number, right: number } | null} Helligkeit 0 (schwarz) bis 1 (weiß)
 */
function measureEdges(img) {
  const key = img.currentSrc || img.src;
  if (toneCache.has(key)) return toneCache.get(key);
  if (!context || !img.naturalWidth) return null;

  // Bild verkleinert und wie im Layout zugeschnitten auf die Leinwand zeichnen
  const box = img.getBoundingClientRect();
  const width = 48;
  const height = Math.max(1, Math.round((width * box.height) / box.width));
  const scale = Math.max(width / img.naturalWidth, height / img.naturalHeight);
  const drawWidth = img.naturalWidth * scale;
  const drawHeight = img.naturalHeight * scale;
  canvas.width = width;
  canvas.height = height;

  try {
    context.drawImage(img, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    // Pfeil-Bereich: 24px Pfeil, 5px vom Rand → in Leinwand-Pixel umgerechnet
    const ratio = width / box.width;
    const size = Math.max(1, Math.round(24 * ratio));
    const inset = Math.round(5 * ratio);
    const top = Math.round(height / 2 - size / 2);
    const result = {
      left: averageLuminance(context.getImageData(inset, top, size, size).data),
      right: averageLuminance(context.getImageData(width - inset - size, top, size, size).data),
    };
    toneCache.set(key, result);
    return result;
  } catch {
    // Bild von fremder Domain ohne CORS: nicht messbar, Standardfarbe bleibt
    return null;
  }
}

/** @param {Uint8ClampedArray} data */
function averageLuminance(data) {
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
  }
  return sum / (data.length / 4);
}

if (!customElements.get('sd-slider')) {
  customElements.define('sd-slider', SdSlider);
}
